import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// STATES_INDEX is loaded dynamically below so this lightweight summary
// (slug, name, region, summary, hardness range, counts) becomes its own
// chunk — separate from the per-state full records under
// src/data/states/<slug>.js that StatePage and CityPage load on demand.
// Session 35 splits what was a single ~135 KB gzip states.js into one
// shared ~5 KB gzip index + 50 per-state micro-chunks.
import { CONTRIBUTORS } from '../data/contributors.js';
import StaticSeo from '../components/StaticSeo.jsx';
import { STATE_PUBLISHED_HARDNESS } from '../data/statePublishedHardness.js';

// Super-region buckets used to group the 14 granular region values from
// states.js into a scannable index page. The granular region label is still
// shown as a sub-heading under each super-region so the editorial coverage
// signal (Mid-South = Cal, Appalachian = Wade, etc.) is preserved.
//
// Bucket order is roughly east-to-west, north-to-south within each tier,
// the way an American reader scans a US map. Any region not listed here is
// dropped into "Other" at the bottom, which is a graceful-failure path.
const SUPER_REGIONS = [
  {
    name: 'Northeast & New England',
    blurb: 'The corridor from Boston to Philadelphia plus the six New England states. Heritage residential stock here is among the deepest in the country.',
    regions: ['New England', 'Northeast'],
  },
  {
    name: 'Mid-Atlantic & Appalachia',
    blurb: 'The Mid-Atlantic corridor and the Appalachian belt that runs through it. Mixed chemistry, deep heritage, and coal-corridor industrial residue where the topography demands it.',
    regions: ['Mid-Atlantic', 'Appalachian', 'Ohio Valley'],
  },
  {
    name: 'Southeast',
    blurb: 'The Atlantic coastal Southeast from the Carolinas through the Gulf-adjacent Deep South. Humidity-dominated drying tails and substantial pre-1900 heritage.',
    regions: ['Southeast'],
  },
  {
    name: 'South & Mid-South',
    blurb: 'The Gulf, the Mid-South, and the lower Mississippi Valley. The hardest-working summer production calendars in the country.',
    regions: ['South', 'Mid-South'],
  },
  {
    name: 'Midwest & Upper Midwest',
    blurb: 'The Great Lakes, the upper Midwest, and the Plains. Continuous water-chemistry gradients running from Chicago lake-source supply to the Bakken-adjacent Dakotas.',
    regions: ['Upper Midwest', 'Midwest'],
  },
  {
    name: 'Mountain West & Southwest',
    blurb: 'High-elevation UV-accelerated IGU degradation, hard mineral water across most of the corridor, and the country\u2019s most operationally distinctive ultra-luxury second-home commercial book.',
    regions: ['Mountain West', 'Southwest'],
  },
  {
    name: 'West & Pacific Northwest',
    blurb: 'The Pacific corridor from Seattle to San Diego plus the desert Southwest extension. Marine-layer chemistry, salt-spray coastal exposure, and the densest coated-glass IGU concentration in the country.',
    regions: ['Pacific Northwest', 'West'],
  },
];

// All region values that appear in one of the buckets above. Anything else
// falls into the "Other" bucket as a safety net.
const BUCKETED_REGIONS = new Set(SUPER_REGIONS.flatMap((b) => b.regions));

// Build a lookup of contributor slug -> display name so we can render an
// editorial-attribution pill on each state row without a second loop.
const CONTRIBUTOR_NAMES = CONTRIBUTORS.reduce((acc, c) => {
  acc[c.slug] = c.name;
  return acc;
}, {});

export default function StateIndex() {
  const [dark, setDark] = useState(false);
  const [states, setStates] = useState(null);

  // Dynamic-import the state-data index module so it becomes its own chunk.
  // The /states index renders a header and a coverage-notes section that
  // do not depend on the data — those render immediately. The bucketed
  // state list waits for the chunk and renders once `states` resolves.
  // After session 35 the loaded module is the lightweight STATES_INDEX
  // only (~11 KB gzip), not the 135 KB gzip 50-state monolith it replaced.
  useEffect(() => {
    let cancelled = false;
    import('../data/states/index.js').then((mod) => {
      if (!cancelled) setStates(mod.STATES_INDEX);
    });
    return () => { cancelled = true; };
  }, []);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  // Group all states by their granular region label first. Guarded on
  // `states` being loaded so the page can render its frame before the
  // data chunk arrives.
  const byRegion = (states || []).reduce((acc, s) => {
    (acc[s.region] ||= []).push(s);
    return acc;
  }, {});

  // Sort within each region: alphabetical by display name.
  Object.values(byRegion).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));

  // Compute the buckets actually present. Skip a bucket entirely if none of
  // its constituent regions have states. This is the path that lets the
  // page render correctly even if a region label gets renamed or retired.
  const renderedBuckets = SUPER_REGIONS
    .map((bucket) => {
      const presentRegions = bucket.regions.filter((r) => byRegion[r] && byRegion[r].length > 0);
      const states = presentRegions.flatMap((r) => byRegion[r]);
      return { ...bucket, presentRegions, states };
    })
    .filter((b) => b.states.length > 0);

  // Anything in the data that wasn't claimed by a bucket goes into "Other".
  // This is a safety net so a new region added in states.js can never make
  // states silently disappear from this page.
  const otherRegions = Object.keys(byRegion).filter((r) => !BUCKETED_REGIONS.has(r));
  const otherStates = otherRegions.flatMap((r) => byRegion[r]);

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <StaticSeo schemaType="collectionPage" path="/states" />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.15em', color: T.inkFaint,
          marginBottom: 36,
        }}>
          <Link to="/" style={{ color: T.inkFaint, textDecoration: 'none' }}>GUIDE</Link>
          {' / '}
          STATES
        </div>

        <span style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
        }}>
          STATE PAGES
        </span>

        <h1 style={{
          fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
          lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: '12px 0 16px',
        }}>
          Window cleaning, by state
        </h1>

        <p style={{
          fontFamily: fontDisplay, fontSize: 20, fontWeight: 300,
          lineHeight: 1.5, color: T.inkSoft,
          margin: '0 0 32px',
        }}>
          Every state has its own water, its own weather, and its own ways that windows fail. These are the long-form pages where one of our contributors walks through how cleaning actually works on the ground in their region.
        </p>

        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.05em', color: T.inkFaint,
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`,
          marginBottom: 40,
        }}>
          {states ? `${states.length} of 50` : '50 of 50'} states published. Coverage is complete.
        </div>

        {/* Super-region sections */}
        {renderedBuckets.map((bucket) => (
          <section key={bucket.name} style={{ marginBottom: 56 }}>
            <h2 style={{
              fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
              letterSpacing: '-0.015em', color: T.ink,
              margin: '0 0 8px',
            }}>
              {bucket.name}
            </h2>
            <p style={{
              fontFamily: fontBody, fontSize: 14, lineHeight: 1.55,
              color: T.inkSoft, margin: '0 0 24px',
              maxWidth: 620,
            }}>
              {bucket.blurb}
            </p>

            {/* For each granular region under this bucket, render its states. */}
            {bucket.presentRegions.map((regionLabel) => (
              <div key={regionLabel} style={{ marginBottom: 24 }}>
                <h3 style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.2em', color: T.sienna,
                  margin: '0 0 4px',
                  textTransform: 'uppercase',
                }}>
                  {regionLabel} <span style={{ color: T.inkFaint, marginLeft: 4 }}>
                    · {byRegion[regionLabel].length}
                  </span>
                </h3>

                {byRegion[regionLabel].map((state) => (
                  <StateRow key={state.slug} state={state} T={T}
                    fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono} />
                ))}
              </div>
            ))}
          </section>
        ))}

        {/* Other — graceful-failure safety net for regions not in the bucket map. */}
        {otherStates.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <h2 style={{
              fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
              letterSpacing: '-0.015em', color: T.ink,
              margin: '0 0 24px',
            }}>
              Other
            </h2>
            {otherRegions.map((regionLabel) => (
              <div key={regionLabel} style={{ marginBottom: 24 }}>
                <h3 style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.2em', color: T.sienna,
                  margin: '0 0 4px',
                  textTransform: 'uppercase',
                }}>
                  {regionLabel} <span style={{ color: T.inkFaint, marginLeft: 4 }}>
                    · {byRegion[regionLabel].length}
                  </span>
                </h3>
                {byRegion[regionLabel].map((state) => (
                  <StateRow key={state.slug} state={state} T={T}
                    fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono} />
                ))}
              </div>
            ))}
          </section>
        )}

        {/* Coverage notes — state-page corpus is closed at 50/50. */}
        <section style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`, marginBottom: 56,
        }}>
          <h2 style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.2em', color: T.inkFaint,
            margin: '0 0 16px', textTransform: 'uppercase',
          }}>
            Coverage Notes
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '0 0 12px',
          }}>
            All fifty states are now covered. The most recent coverage extends the corpus into the Pacific-extension states — Alaska across four working zones (Anchorage and Cook Inlet, the Fairbanks ice-fog interior, the Juneau and Southeast Panhandle rainforest, and the Aleutian and Cook Inlet volcanic-ash overlay) and Hawaii across four-island geography (Oahu, Maui, the Big Island Kona-Hilo split with continuous Kilauea vog residue, and Kauai with the Mount Waialeale rainforest pattern). The Hawaii coverage establishes the framework reference for chronic chloride-aerosol handling at substantially higher intensity than the Gulf Coast pattern.
          </p>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '0',
          }}>
            For the operating protocols that cut across regional chemistry, see the <Link to="/articles" style={{ color: T.sienna }}>article index</Link> and the <Link to="/glossary" style={{ color: T.sienna }}>glossary</Link>. For the editorial team that writes these state pages, see the <Link to="/contributors" style={{ color: T.sienna }}>contributors</Link> page.
          </p>
        </section>

        {/* Footer */}
        <footer style={{
          marginTop: 40, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
        }}>
          <Link to="/" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO WINDOW WASHING GUIDE
          </Link>
        </footer>
      </div>
    </div>
  );
}

// Individual state-row component. Pulled out so the bucket render loop
// and the "Other" fallback both render the same way. Reads from the
// lightweight STATES_INDEX entry shape (flat fields, no nested
// waterProfile / topCities / regionalContaminants arrays) — see the
// emitted shape in src/data/states/index.js.
function StateRow({ state, T, fontDisplay, fontBody, fontMono }) {
  const contributorName = CONTRIBUTOR_NAMES[state.assignedAuthor];

  return (
    <Link
      to={`/states/${state.slug}`}
      style={{
        display: 'block',
        padding: '20px 0',
        borderTop: `1px solid ${T.rule}`,
        textDecoration: 'none', color: T.ink,
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'baseline', gap: 16, marginBottom: 6,
        flexWrap: 'wrap',
      }}>
        <h4 style={{
          fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
          margin: 0, color: T.ink, letterSpacing: '-0.01em',
        }}>
          {state.name}
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, marginLeft: 8, letterSpacing: '0.08em',
          }}>
            {state.abbreviation}
          </span>
        </h4>
        <div style={{
          fontFamily: fontMono, fontSize: 12,
          color: T.sienna, letterSpacing: '0.03em',
        }}>
          {/* s136 (DECISIONS #58): published range where a covered utility publishes one; else the planning range, labelled */}
          {(() => {
            const p = STATE_PUBLISHED_HARDNESS[state.slug];
            if (p && p.n) return <>{p.lo === p.hi ? p.lo : `${p.lo}–${p.hi}`} mg/L <span style={{ color: T.inkFaint, fontSize: 10, letterSpacing: '0.1em' }}>PUBLISHED</span></>;
            return <span style={{ color: T.inkFaint }}>{state.hardnessLow}–{state.hardnessHigh} mg/L <span style={{ fontSize: 10, letterSpacing: '0.1em' }}>PLANNING</span></span>;
          })()}
        </div>
      </div>

      <p style={{
        fontFamily: fontBody, fontSize: 14, lineHeight: 1.55,
        color: T.inkSoft, margin: '0 0 8px',
      }}>
        {state.summary}
      </p>

      <div style={{
        fontFamily: fontMono, fontSize: 10,
        letterSpacing: '0.12em', color: T.inkFaint,
        display: 'flex', flexWrap: 'wrap', gap: 12,
      }}>
        <span>{state.topCitiesCount} CITIES</span>
        <span>{state.contaminantsCount} CONTAMINANTS</span>
        {contributorName && (
          <span style={{ color: T.sage }}>
            BY {contributorName.toUpperCase()}
          </span>
        )}
      </div>
    </Link>
  );
}
